import { useEffect, useMemo, useRef, useState } from 'react';
import { Logo } from '../Logo';
import * as q from '../upload/queue';
import { Uploader } from '../upload/uploader';

type EventInfo = {
  slug: string;
  name: string;
  date: string | null;
  licence: { version: string; text: string; peoplePrompt: string };
  partSize: number;
};

export function GuestUpload({ slug }: { slug: string }) {
  const [info, setInfo] = useState<EventInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [items, setItems] = useState<q.QueueItem[]>([]);
  const [name, setName] = useState('');
  const [people, setPeople] = useState<'yes' | 'no' | 'unknown'>('unknown');
  const [agreed, setAgreed] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [storageOk, setStorageOk] = useState(true);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploaderRef = useRef<Uploader | null>(null);

  useEffect(() => {
    fetch(`/api/e/${slug}`)
      .then((r) => (r.ok ? (r.json() as Promise<EventInfo>) : Promise.reject(r.status)))
      .then(setInfo)
      .catch(() => setNotFound(true));
    q.usable().then(setStorageOk);
  }, [slug]);

  // Remember the guest's name between visits — they will come back the next day.
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ec-name');
      if (saved) setName(saved);
    } catch { /* private mode: not important enough to care */ }
  }, []);

  useEffect(() => {
    if (!info) return;
    const up = new Uploader(slug, info.partSize);
    uploaderRef.current = up;
    const off = up.onChange(setItems);
    q.all(slug).then(setItems);
    up.drain();

    const onOnline = () => { setOnline(true); up.drain(); };
    const onOffline = () => setOnline(false);
    const onVisible = () => { if (document.visibilityState === 'visible') up.drain(); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      off();
      up.stop();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [info, slug]);

  const counts = useMemo(() => {
    const done = items.filter((i) => i.state === 'done').length;
    const failed = items.filter((i) => i.state === 'failed').length;
    const working = items.length - done - failed;
    return { done, failed, working, total: items.length };
  }, [items]);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!files.length || !info) return;
    try { localStorage.setItem('ec-name', name.trim()); } catch { /* fine */ }
    await q.enqueue(slug, files, { name: name.trim(), people, device: q.deviceId() });
    setItems(await q.all(slug));
    uploaderRef.current?.drain();
  }

  if (notFound) {
    return (
      <div className="page guest">
        <div className="shell guest-inner">
          <Logo size={30} />
          <h1>That link has no event.</h1>
          <p className="lede">Check the code on the card, or ask whoever gave it to you.</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="page guest">
        <div className="shell guest-inner">
          <Logo size={30} />
          <p className="note">Loading&hellip;</p>
        </div>
      </div>
    );
  }

  const ready = agreed && storageOk;

  return (
    <div className="page guest">
      <div className="shell guest-inner">
        <Logo size={30} />
        <h1>{info.name}</h1>
        {info.date && <p className="note">{info.date}</p>}

        <p className="lede">Add the photos and videos you took. No app, no account.</p>

        {!storageOk && (
          <p className="warn" role="alert">
            This browser will not let us hold your photos while they upload. Private browsing usually
            causes that &mdash; try a normal window.
          </p>
        )}

        {!online && (
          <p className="warn" role="status">
            You are offline. Anything you add is saved on this phone and will finish by itself when
            you have signal again.
          </p>
        )}

        <div className="field">
          <label htmlFor="g-name">Your name (optional)</label>
          <input
            id="g-name"
            type="text"
            autoComplete="name"
            placeholder="So they know who to thank"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <fieldset className="field">
          <legend>{info.licence.peoplePrompt}</legend>
          <div className="choices">
            {(['yes', 'no'] as const).map((v) => (
              <label key={v} className={people === v ? 'choice on' : 'choice'}>
                <input
                  type="radio"
                  name="people"
                  value={v}
                  checked={people === v}
                  onChange={() => setPeople(v)}
                />
                {v === 'yes' ? 'Yes, others are in them' : 'No, just me or the room'}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="consent">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>{info.licence.text}</span>
        </label>

        <button className="add" type="button" disabled={!ready} onClick={() => fileRef.current?.click()}>
          Add photos &amp; videos
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          onChange={pick}
        />
        {!agreed && <p className="note">Tick the box above to start.</p>}

        {counts.total > 0 && (
          <div className="progress">
            <p className="count">
              {counts.done} of {counts.total} added
              {counts.working > 0 && <span className="muted"> &middot; {counts.working} going up</span>}
              {counts.failed > 0 && <span className="bad"> &middot; {counts.failed} stuck</span>}
            </p>
            <ul className="files">
              {items.map((i) => (
                <li key={i.id} className={`file ${i.state}`}>
                  <span className="fname">{i.filename}</span>
                  <span className="fstate">
                    {i.state === 'done'
                      ? 'added'
                      : i.state === 'failed'
                        ? 'stuck — will retry'
                        : !online
                          ? 'waiting for signal'
                          : i.size > info.partSize && i.sent > 0
                            ? `${Math.round((i.sent / i.size) * 100)}%`
                            : 'sending'}
                  </span>
                </li>
              ))}
            </ul>
            {counts.done === counts.total && (
              <p className="done-note">All done. You can close this — thank you.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
